import React, { useState } from 'react'
import { Formik } from 'formik'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { editChargingCardApi } from '../api/edit-charging-card.api'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useDispatch } from 'react-redux'
import { refreshApiView } from '@e3dc-react/shell/State/ApiView/ApiViewActions'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { EditChargingCardFormData, EMobilityChargingCardsDataModel } from '../models/e-mobility-charging-cards-models'
import { EditChargingCardForm } from './EditChargingCardForm'
import { useEmobilityChargingCardEditorFormInitializer } from 'src/Elements/P3_eMobility/emobility-charging-cards/hooks'

export interface EMobilityChargingCardEditorProps {
  open: boolean
  onClose: () => void
  place: number
  data: EMobilityChargingCardsDataModel
  serialNo: string
}

const EMobilityChargingCardEditor: React.VFC<EMobilityChargingCardEditorProps> = ({ open, place, onClose, data, serialNo }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const dispatch = useDispatch()
  const authFetch = useAuthFetchWithResponse()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const { t } = useCustomTranslation()
  const { validationSchema, initialValues } = useEmobilityChargingCardEditorFormInitializer(data)

  const handleSubmit = async (formData: EditChargingCardFormData): Promise<void> => {
    setIsLoading(true)
    try {
      await editChargingCardApi(data, formData, serialNo, authFetch, initialValues)
      enqueueSuccessStack(t('emobility.chargingCards.edit.successMessage'))
      dispatch(refreshApiView())
      onClose()
    } catch {
      enqueueErrorStack(t('emobility.chargingCards.edit.errorMessage'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
      {({ handleSubmit, isValid, dirty }) => {
        return (
          <Dialog
            title={t('emobility.chargingCards.edit.title', { chargingCardName: initialValues.chargingCardName, rfid: initialValues.rfid })}
            onClose={onClose}
            confirmClose={dirty}
            place={place}
            actions={{
              primary: {
                isLoading: isLoading,
                disabled: !dirty || !isValid,
                name: t('emobility.chargingCards.edit.save'),
                onClick: handleSubmit,
              },
              secondary: {
                name: t('emobility.chargingCards.edit.cancel'),
              },
            }}
          >
            <EditChargingCardForm status={initialValues.status} />
          </Dialog>
        )
      }}
    </Formik>
  )
}

export default EMobilityChargingCardEditor
